import React from 'react';

import { Badge, createStyles, Paper, StackProps, Text, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconBuildingBank,
  IconEdit,
  IconLock,
  IconLockOpen,
  IconTrashX,
} from '@tabler/icons-react';
import Menu, { MenuItemProps } from 'components/Menu';
import { useAuth } from 'context/auth-context';
import { AthleteRetrieve } from 'types';

const useStyles = createStyles(theme => ({
  header: {
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'min-content auto min-content min-content',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '85px 1fr',
    gap: theme.spacing.lg,
    alignItems: 'center',
  },
}));

interface IBacsDebitPaymentMethodCardProps extends StackProps {
  name: string;
  last4: string;
  sortCode: string;
  isDefault: boolean;
  paymentMethodId: string;
  athlete?: AthleteRetrieve;
  openEditPaymentMethodForm: (paymentMethodId: string) => void;
  openPaymentMethodDeleteModal?: (paymentMethodId: string) => void;
  permission?: boolean;
  isLocked?: boolean;
  failureMessage?: string | null;
  handleUnlock: (paymentMethodId: string) => void;
}

const formatSortCode = (sortCode: string): string => {
  const cleaned = sortCode.replace(/\D/g, '');
  return cleaned.match(/.{1,2}/g)?.join('-') || sortCode;
};

const BacsDebitPaymentMethodCard = ({
  name,
  last4,
  sortCode,
  paymentMethodId,
  isDefault,
  athlete,
  openEditPaymentMethodForm,
  openPaymentMethodDeleteModal,
  handleUnlock,
  permission = true,
  isLocked = false,
  failureMessage = null,
}: IBacsDebitPaymentMethodCardProps): JSX.Element => {
  const { session } = useAuth();
  const { classes, theme } = useStyles();

  const getMenuItems = (): MenuItemProps[] => {
    const actions: MenuItemProps[] = [
      {
        title: 'Edit',
        icon: <IconEdit size={theme.fontSizes.sm} />,
        onClick: () => openEditPaymentMethodForm(paymentMethodId),
      },
    ];
    if (isLocked) {
      actions.push({
        title: 'Unlock Actions',
        icon: <IconLockOpen size={theme.fontSizes.sm} />,
        onClick: () => handleUnlock(paymentMethodId),
        disabled: !permission,
      });
    }
    if (session?.role !== 'athlete') {
      actions.push({
        title: 'Remove',
        icon: <IconTrashX size={theme.fontSizes.sm} />,
        color: 'red',
        onClick: () => openPaymentMethodDeleteModal?.(paymentMethodId),
      });
    }
    return actions;
  };

  return (
    <Paper
      shadow='sm'
      radius='lg'
      p='md'
      withBorder
      bg={isLocked ? 'red.1' : failureMessage ? 'yellow.1' : 'white'}
    >
      <div className={classes.header}>
        {isLocked ? (
          <IconLock color={theme.colors.red[6]} />
        ) : failureMessage ? (
          <Tooltip label={failureMessage}>
            <IconAlertCircle color={theme.colors.yellow[6]} />
          </Tooltip>
        ) : (
          <IconBuildingBank />
        )}

        <Text truncate>{name}</Text>

        {isDefault ? (
          <Badge size='sm' variant='outline' color='kiloBlue'>
            Default
          </Badge>
        ) : (
          <div />
        )}

        <Menu
          position='bottom-end'
          items={getMenuItems()}
          disabled={!permission || !!athlete?.primaryAccount}
          color={isLocked ? 'red.8' : 'kiloBlue'}
        />
      </div>

      <div className={classes.row}>
        <Text fs='italic' c='dimmed' fz='xs' align='right'>
          Account Number
        </Text>
        <Text fz='sm' ff='monospace'>
          XXXXXX{last4}
        </Text>
      </div>

      <div className={classes.row}>
        <Text fs='italic' c='dimmed' fz='xs' align='right'>
          Sort Code
        </Text>
        <Text fz='sm' ff='monospace'>
          {formatSortCode(sortCode)}
        </Text>
      </div>

      {failureMessage && (
        <Text fz='xs' c='yellow.9' mt='xs' ta='center'>
          {failureMessage}
        </Text>
      )}
    </Paper>
  );
};

export default BacsDebitPaymentMethodCard;
